import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { attributionColumns, attributionFields } from "@/lib/attribution-schema";

const businessChecklistSchema = z.object({
  name: z.string().trim().min(2, "Please tell us your name").max(120),
  email: z.string().trim().email("Please enter a valid email address").max(200),
  phone: z.string().trim().max(40).optional().or(z.literal("")),
  side: z.enum(["buying", "selling", "both", "adviser"]),
  dealStage: z.enum([
    "just-researching",
    "negotiating-terms",
    "contract-received",
    "under-contract",
    "due-diligence",
    "heading-to-settlement",
  ]),
  businessType: z.string().trim().max(120).optional().or(z.literal("")),
  message: z.string().trim().max(4000).optional().or(z.literal("")),
  sourcePage: z.string().trim().max(200).optional().or(z.literal("")),
  ...attributionFields,
});

export type BusinessChecklistInput = z.infer<typeof businessChecklistSchema>;

export const submitBusinessChecklistRequest = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => businessChecklistSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { error } = await supabaseAdmin.from("business_checklist_requests").insert({
      name: data.name,
      email: data.email,
      phone: data.phone || null,
      side: data.side,
      deal_stage: data.dealStage,
      business_type: data.businessType || null,
      message: data.message || null,
      source_page: data.sourcePage || null,
      ...attributionColumns(data),
    });

    if (error) {
      console.error("Failed to store business checklist request", error);
      throw new Error("We could not record your request. Please call us instead.");
    }

    const { sendLeadToCrm } = await import("@/lib/crm.server");
    await sendLeadToCrm({
      formType: "business-checklist-download",
      name: data.name,
      email: data.email,
      phone: data.phone,
      matterType: "business-commercial",
      sourcePage: data.sourcePage,
      message: data.message,
      details: {
        side: data.side,
        deal_stage: data.dealStage,
        business_type: data.businessType,
      },
    });

    return { ok: true } as const;
  });
